// src/Auth/Signup/Signup.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../utils/AuthContext";
import { toast } from "react-toastify";
import {url} from "../../Constant/TextConstant";
import {
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Typography,
  Box,
  Container,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { apiFetch } from "../../utils/apiClient.js";

const Signup = () => {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [gender, setGender] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [waitingTime, setWaitingTime] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const { signup } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let timer;
    if (waitingTime > 0) {
      timer = setTimeout(() => setWaitingTime(waitingTime - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [waitingTime]);

  const calculateWaitingTime = (attempts) => {
    if (attempts === 1) return 15;
    if (attempts === 2) return 30;
    if (attempts === 3) return 60;
    return 300;
  };

  const sendOtp = async () => {
    if (waitingTime > 0) {
      toast.error(
        `Please wait ${waitingTime} seconds before requesting another OTP.`
      );
      return;
    }

    if (!/^\d{10}$/.test(mobile)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiFetch(
        `${url.baseAPIUrl}/api/LoginMaster/getotp?mobile=${mobile}`
      );
      const data = await response.json();

      if (response.ok && data.Message === "OTP sent successfully.") {
        setOtpSent(true);
        setAttempts((prev) => prev + 1);
        setWaitingTime(calculateWaitingTime(attempts + 1));
        toast.success(`OTP sent successfully to ${mobile}`);
      } else {
        throw new Error(data.Message || "Failed to send OTP");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = () => {
    if (otp.length === 6) {
      // In a real app, you would verify with your backend
      setOtpVerified(true);
      toast.success("Mobile number verified successfully!");
    } else {
      toast.error("Please enter a valid 6-digit OTP");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!otpVerified) {
      toast.error("Please verify your mobile number first");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (!name || !mobile || !password || !gender) {
      toast.error("All fields are required");
      return;
    }

    setIsLoading(true);
    try {
      const success = await signup(name, mobile, password, gender);
      if (success) {
        navigate("/login");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Box
        sx={{
          p: 4,
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: "background.paper",
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold" }}>
          Join Us Now!
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Sign up to access exclusive benefits and personalized services.
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <TextField
            fullWidth
            margin="normal"
            label="Full Name"
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Mobile Number"
            variant="outlined"
            value={mobile}
            onChange={(e) => {
              setMobile(e.target.value);
              if (otpSent && e.target.value !== mobile) {
                setOtpSent(false);
                setOtpVerified(false);
              }
            }}
            inputProps={{ maxLength: 10 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">+91</InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    onClick={sendOtp}
                    disabled={otpSent || waitingTime > 0}
                    size="small"
                  >
                    {otpSent
                      ? "Sent"
                      : waitingTime > 0
                      ? `Resend in ${waitingTime}s`
                      : "Send OTP"}
                  </Button>
                </InputAdornment>
              ),
            }}
          />

          {otpSent && (
            <TextField
              fullWidth
              margin="normal"
              label="OTP"
              variant="outlined"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              inputProps={{ maxLength: 6 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      onClick={verifyOtp}
                      disabled={otpVerified}
                      size="small"
                    >
                      {otpVerified ? "Verified" : "Verify"}
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          )}

          <FormControl fullWidth margin="normal">
            <InputLabel>Gender</InputLabel>
            <Select
              value={gender}
              label="Gender"
              onChange={(e) => setGender(e.target.value)}
            >
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
              <MenuItem value="Transgender">Transgender</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            margin="normal"
            label="Password"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={!otpVerified}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Confirm Password"
            type={showConfirmPassword ? "text" : "password"}
            variant="outlined"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={!otpVerified}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={isLoading || !otpVerified}
            sx={{ mt: 3, py: 1.5 }}
          >
            {isLoading ? <CircularProgress size={24} /> : "Sign Up"}
          </Button>

          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography variant="body2">
              Already have an account?{" "}
              <Link to="/login" style={{ textDecoration: "none" }}>
                <Typography
                  component="span"
                  color="primary"
                  sx={{ fontWeight: "bold" }}
                >
                  Login
                </Typography>
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default Signup;
